import { Box, Loader, Select } from '@mantine/core'
import { useRouter } from 'next/router'
import React, { forwardRef, useState } from 'react'
import { SWRHook } from 'swr'

import { PictureCard } from '@/components/surfaces'
import { useFetch } from '@/lib/hooks'
import { stringCompare, swrCallbackMiddleware } from '@/lib/utils'
import { Channel as ChannelType } from '@/types'

type Props = {
  selected?: ChannelType | null
  onChange?: (newValue: ChannelType) => void
  inputProps?: any
}

export default function SiteChannelAutocomplete({ selected, onChange, inputProps }: Props) {
  // Hooks
  const router = useRouter()
  const { site: siteSlug } = router.query || {}

  // States
  const [search, setSearch] = useState('')

  // Fetch callback
  const [dataResults, setDataResults] = useState<any[]>([])
  const dataCallback = (data: any) => {
    const results = [...data?.results, ...dataResults].filter(
      (channel, index, array) => index === array.findIndex(item => item?.uid === channel?.uid)
    )
    setDataResults(results)
  }

  // Fetch
  const { data, error } = useFetch(
    [siteSlug ? `/${siteSlug}/channels/` : null, { page_size: 40, search }],
    { use: [(useSWRNext: SWRHook) => swrCallbackMiddleware(dataCallback)(useSWRNext)] }
  )

  // Constants
  const loading = !data && !error
  const formattedData = Array.isArray(dataResults)
    ? [...(selected ? [{ ...selected }] : []), ...dataResults]
        .map((channel: any) => ({
          ...channel,
          label: `${channel?.title}`,
          value: channel?.uid
        }))
        .filter(
          (channel, index, array) => index === array.findIndex(item => item?.uid === channel?.uid)
        )
        .sort((a, b) => b?.title - a?.title)
    : []

  // eslint-disable-next-line react/display-name
  const SelectItem = forwardRef<HTMLDivElement, any>(
    (
      {
        uid,
        site,
        institution,
        title,
        description,
        slug,
        privacy,
        isUnlisted,
        maintenance,
        maintenanceMessage,
        logo,
        cover,
        absoluteUrl,
        displayBanner,
        ...others
      }: any,
      ref
    ) => (
      <Box p="xs" ref={ref} {...others}>
        <PictureCard
          title={title}
          picture={logo}
        />
      </Box>
    )
  )

  // Actions
  const handleChange = (value: string) =>
    onChange?.(formattedData.find(channel => channel?.uid === value))

  return (
    <Select
      placeholder="Selecionar canal"
      itemComponent={SelectItem}
      data={formattedData}
      clearable
      searchable
      nothingFound={loading ? 'Buscando...' : 'Nenhum resultado'}
      searchValue={search}
      onSearchChange={setSearch}
      filter={(_, item) => stringCompare(`${item?.title}`, search)}
      rightSection={loading && <Loader size="xs" />}
      {...inputProps}
      value={selected?.uid || null}
      onChange={handleChange}
      styles={theme => ({
        item: {
          '&[data-selected]': {
            '&, &:hover': { backgroundColor: theme.colors.blue[1], color: theme.white }
          }
        }
      })}
    />
  )
}
